import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * ElevationStats - Performance monitoring for the Elevations Viewer
 *
 * Tracks SVG fetch times, processing times, and render performance.
 * Toggle visibility with the 's' key.
 */

// Global performance tracker that can be updated from InlineSVGProjection
const performanceData = {
  svgFetches: [],
  svgProcessing: [],
  lastRenderTime: 0,
  objectCount: 0,
  svgProjectionCount: 0,
  fallbackCount: 0,
};

// Export functions to record performance data from other components
export const recordSvgFetch = (url, duration) => {
  performanceData.svgFetches.push({ url, duration, timestamp: Date.now() });
  // Keep only last 50 entries
  if (performanceData.svgFetches.length > 50) {
    performanceData.svgFetches.shift();
  }
};

export const recordSvgProcessing = (url, duration) => {
  performanceData.svgProcessing.push({ url, duration, timestamp: Date.now() });
  // Keep only last 50 entries
  if (performanceData.svgProcessing.length > 50) {
    performanceData.svgProcessing.shift();
  }
};

export const recordRenderStats = (stats) => {
  performanceData.lastRenderTime = stats.renderTime || 0;
  performanceData.objectCount = stats.objectCount || 0;
  performanceData.svgProjectionCount = stats.svgProjectionCount || 0;
  performanceData.fallbackCount = stats.fallbackCount || 0;
};

export const clearPerformanceData = () => {
  performanceData.svgFetches = [];
  performanceData.svgProcessing = [];
};

const ElevationStats = ({ visible = false }) => {
  const [stats, setStats] = useState({
    avgFetchTime: 0,
    maxFetchTime: 0,
    avgProcessTime: 0,
    maxProcessTime: 0,
    totalFetches: 0,
    renderTime: 0,
    objectCount: 0,
    svgProjectionCount: 0,
    fallbackCount: 0,
    fps: 0,
  });

  const frameTimesRef = useRef([]);
  const lastFrameTimeRef = useRef(performance.now());
  const animationFrameRef = useRef(null);

  const updateStats = useCallback(() => {
    // Calculate FPS
    const now = performance.now();
    const deltaTime = now - lastFrameTimeRef.current;
    lastFrameTimeRef.current = now;

    frameTimesRef.current.push(deltaTime);
    if (frameTimesRef.current.length > 60) {
      frameTimesRef.current.shift();
    }

    const avgFrameTime =
      frameTimesRef.current.reduce((a, b) => a + b, 0) /
      frameTimesRef.current.length;
    const fps = 1000 / avgFrameTime;

    // Calculate SVG fetch stats (last 10 seconds)
    const recentTime = Date.now() - 10000;
    const recentFetches = performanceData.svgFetches.filter(
      (f) => f.timestamp > recentTime
    );
    const recentProcessing = performanceData.svgProcessing.filter(
      (p) => p.timestamp > recentTime
    );

    const avgFetchTime =
      recentFetches.length > 0
        ? recentFetches.reduce((sum, f) => sum + f.duration, 0) /
          recentFetches.length
        : 0;

    const maxFetchTime =
      recentFetches.length > 0
        ? Math.max(...recentFetches.map((f) => f.duration))
        : 0;

    const avgProcessTime =
      recentProcessing.length > 0
        ? recentProcessing.reduce((sum, p) => sum + p.duration, 0) /
          recentProcessing.length
        : 0;

    const maxProcessTime =
      recentProcessing.length > 0
        ? Math.max(...recentProcessing.map((p) => p.duration))
        : 0;

    setStats({
      avgFetchTime,
      maxFetchTime,
      avgProcessTime,
      maxProcessTime,
      totalFetches: performanceData.svgFetches.length,
      renderTime: performanceData.lastRenderTime,
      objectCount: performanceData.objectCount,
      svgProjectionCount: performanceData.svgProjectionCount,
      fallbackCount: performanceData.fallbackCount,
      fps: Math.round(fps),
    });

    if (visible) {
      animationFrameRef.current = requestAnimationFrame(updateStats);
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      animationFrameRef.current = requestAnimationFrame(updateStats);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [visible, updateStats]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '10px',
        right: '10px',
        background: 'rgba(0,0,0,0.85)',
        color: '#0f0',
        padding: '10px 14px',
        fontFamily: 'monospace',
        fontSize: '11px',
        borderRadius: '4px',
        pointerEvents: 'none',
        zIndex: 10000,
        minWidth: '180px',
        border: '1px solid #0f0',
        lineHeight: '1.5',
      }}
    >
      <div
        style={{
          fontWeight: 'bold',
          marginBottom: '6px',
          color: '#0ff',
          borderBottom: '1px solid #0f0',
          paddingBottom: '4px',
        }}
      >
        Elevation Stats
      </div>

      {/* FPS */}
      <div style={{ color: stats.fps < 30 ? '#f55' : '#0f0' }}>
        FPS: {stats.fps}
      </div>

      {/* Object counts */}
      <div style={{ marginTop: '6px', color: '#ff0' }}>Objects</div>
      <div>Total: {stats.objectCount}</div>
      <div>SVG Projections: {stats.svgProjectionCount}</div>
      <div>Fallback boxes: {stats.fallbackCount}</div>

      {/* SVG Fetch times */}
      <div style={{ marginTop: '6px', color: '#ff0' }}>SVG Fetch (10s)</div>
      <div>Avg: {stats.avgFetchTime.toFixed(1)}ms</div>
      <div style={{ color: stats.maxFetchTime > 100 ? '#f55' : '#0f0' }}>
        Max: {stats.maxFetchTime.toFixed(1)}ms
      </div>

      {/* SVG Processing times */}
      <div style={{ marginTop: '6px', color: '#ff0' }}>SVG Process (10s)</div>
      <div>Avg: {stats.avgProcessTime.toFixed(2)}ms</div>
      <div style={{ color: stats.maxProcessTime > 10 ? '#f55' : '#0f0' }}>
        Max: {stats.maxProcessTime.toFixed(2)}ms
      </div>

      {/* Help */}
      <div
        style={{
          marginTop: '8px',
          fontSize: '9px',
          color: '#888',
          borderTop: '1px solid #333',
          paddingTop: '4px',
        }}
      >
        Press 's' to hide
      </div>
    </div>
  );
};

export default ElevationStats;
